<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import CategoryModal from './components/CategoryModal.vue';
import EmergencyKeywordsCard from './components/EmergencyKeywordsCard.vue';
import {
  apiErrorCode,
  apiErrorDetail,
  apiErrorMessage,
} from '../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from './constants';
import { SEVERITIES, usedByKeys } from './catalogForms';
import { TIER_STYLE } from './helpers';

const SEVERITY_STYLE = {
  urgent: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300',
  medium: 'bg-woot-100 text-woot-700 dark:bg-woot-900 dark:text-woot-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300',
};

export default {
  name: 'CoLivingCategoriesPage',
  components: {
    PageHeader,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    CategoryModal,
    EmergencyKeywordsCard,
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      severities: SEVERITIES,
      severityStyle: SEVERITY_STYLE,
      tierStyle: TIER_STYLE,
      search: '',
      severityFilter: '',
      statusFilter: 'all',
      playbookFilter: '',
      loadError: '',
      isModalOpen: false,
      editingCategory: null,
      categoryToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCatalog', {
      categories: 'getCategories',
      uiFlags: 'getUiFlags',
    }),
    visibleCategories() {
      const q = this.search.trim().toLowerCase();
      return this.categories.filter(category => {
        if (this.severityFilter && category.severity !== this.severityFilter) {
          return false;
        }
        if (this.statusFilter === 'active' && !category.active) return false;
        if (this.statusFilter === 'archived' && category.active) return false;
        if (this.playbookFilter === 'ready' && !category.has_playbook) {
          return false;
        }
        if (this.playbookFilter === 'missing' && category.has_playbook) {
          return false;
        }
        return (
          !q ||
          category.key.toLowerCase().includes(q) ||
          category.name.toLowerCase().includes(q)
        );
      });
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions('coLivingCatalog', ['fetchCategories', 'removeCategory']),
    ...mapActions('coLivingConfig', ['fetchSettings']),
    async load() {
      this.loadError = '';
      try {
        await Promise.all([this.fetchCategories(), this.fetchSettings()]);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    openAdd() {
      this.editingCategory = null;
      this.isModalOpen = true;
    },
    openEdit(category) {
      this.editingCategory = category;
      this.isModalOpen = true;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeCategory(this.categoryToDelete.key);
        useAlert(this.$t('CO_LIVING.CATEGORIES.DELETED'));
      } catch (err) {
        if (apiErrorCode(err) === 'in_use') {
          const used = usedByKeys(apiErrorDetail(err))
            .map(key => this.$t(`CO_LIVING.IN_USE.${key}`))
            .join(', ');
          useAlert(this.$t('CO_LIVING.CATEGORIES.IN_USE', { used }));
        } else {
          useAlert(
            apiErrorMessage(this, err, 'CO_LIVING.CATEGORIES.DELETE_FAILED')
          );
        }
      } finally {
        this.isDeleting = false;
        this.categoryToDelete = null;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-categories-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <div class="flex items-start justify-between gap-4 mb-2">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.CATEGORIES.INTRO') }}
      </p>
      <woot-button
        icon="add"
        data-testid="co-living-add-category"
        @click="openAdd"
      >
        {{ $t('CO_LIVING.CATEGORIES.ADD_CATEGORY') }}
      </woot-button>
    </div>
    <p class="mb-4 text-xs text-slate-500 dark:text-slate-400">
      {{ $t('CO_LIVING.CATEGORIES.ROUTING_NOTE') }}
    </p>

    <div class="flex flex-wrap gap-3 mb-4">
      <input
        v-model="search"
        type="search"
        :placeholder="$t('CO_LIVING.CATEGORIES.SEARCH')"
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-category-search"
      />
      <select
        v-model="severityFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-category-severity-filter"
      >
        <option value="">
          {{ $t('CO_LIVING.CATEGORIES.ALL_SEVERITIES') }}
        </option>
        <option v-for="s in severities" :key="s" :value="s">
          {{ $t(`CO_LIVING.CATEGORIES.SEVERITIES.${s.toUpperCase()}`) }}
        </option>
      </select>
      <select
        v-model="playbookFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-category-playbook-filter"
      >
        <option value="">{{ $t('CO_LIVING.CATEGORIES.ALL_PLAYBOOKS') }}</option>
        <option value="ready">{{ $t('CO_LIVING.CATEGORIES.READY') }}</option>
        <option value="missing">
          {{ $t('CO_LIVING.CATEGORIES.MISSING') }}
        </option>
      </select>
      <select
        v-model="statusFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-category-status-filter"
      >
        <option value="all">{{ $t('CO_LIVING.COMMON.ALL_STATUSES') }}</option>
        <option value="active">{{ $t('CO_LIVING.COMMON.ACTIVE') }}</option>
        <option value="archived">{{ $t('CO_LIVING.COMMON.ARCHIVED') }}</option>
      </select>
    </div>

    <LoadingState
      v-if="uiFlags.isFetchingCategories && !categories.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>
    <div v-else class="flex flex-col gap-6">
      <p
        v-if="!categories.length"
        class="text-sm text-slate-500 dark:text-slate-400"
        data-testid="co-living-categories-empty"
      >
        {{ $t('CO_LIVING.CATEGORIES.EMPTY') }}
      </p>
      <p
        v-else-if="!visibleCategories.length"
        class="text-sm text-slate-500 dark:text-slate-400"
      >
        {{ $t('CO_LIVING.COMMON.NO_MATCHES') }}
      </p>
      <div
        v-else
        class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <table class="w-full text-sm" data-testid="co-living-categories-table">
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">
                {{ $t('CO_LIVING.CATEGORIES.COLUMNS.CATEGORY') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.CATEGORIES.COLUMNS.SEVERITY') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.CATEGORIES.COLUMNS.TIER') }}</th>
              <th class="p-3">
                {{ $t('CO_LIVING.CATEGORIES.COLUMNS.PLAYBOOK') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.CATEGORIES.COLUMNS.EMERGENCY') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.CATEGORIES.COLUMNS.ROUTING') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="category in visibleCategories"
              :key="category.key"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-category-row-${category.key}`"
            >
              <td class="p-3">
                {{ category.name }}
                <span class="block font-mono text-xs text-slate-500">
                  {{ category.key }}
                </span>
              </td>
              <td class="p-3">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded"
                  :class="severityStyle[category.severity]"
                >
                  {{
                    $t(
                      `CO_LIVING.CATEGORIES.SEVERITIES.${category.severity.toUpperCase()}`
                    )
                  }}
                </span>
              </td>
              <td class="p-3">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded"
                  :class="tierStyle[category.sla_tier]"
                >
                  {{ category.sla_tier }}
                </span>
              </td>
              <td class="p-3">
                {{
                  category.has_playbook
                    ? $t('CO_LIVING.CATEGORIES.READY')
                    : $t('CO_LIVING.CATEGORIES.MISSING')
                }}
              </td>
              <td class="p-3">
                {{
                  category.emergency
                    ? $t('CO_LIVING.CATEGORIES.IMMEDIATE')
                    : '-'
                }}
              </td>
              <td class="p-3">
                <span
                  v-if="!category.routed"
                  class="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
                  :data-testid="`co-living-category-unrouted-${category.key}`"
                >
                  {{ $t('CO_LIVING.CATEGORIES.UNROUTED') }}
                </span>
                <template v-else>
                  {{ $t('CO_LIVING.CATEGORIES.ROUTED') }}
                </template>
              </td>
              <td class="p-3">
                {{
                  category.active
                    ? $t('CO_LIVING.COMMON.ACTIVE')
                    : $t('CO_LIVING.COMMON.ARCHIVED')
                }}
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  icon="edit"
                  :data-testid="`co-living-category-edit-${category.key}`"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  @click="openEdit(category)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :data-testid="`co-living-category-delete-${category.key}`"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  @click="categoryToDelete = category"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <EmergencyKeywordsCard />
    </div>

    <CategoryModal
      v-if="isModalOpen"
      :category="editingCategory"
      @close="isModalOpen = false"
    />
    <DeleteModal
      v-if="categoryToDelete"
      :delete-name="categoryToDelete.name"
      :is-loading="isDeleting"
      @close="categoryToDelete = null"
      @confirm="confirmDelete"
    >
      <p class="mt-2 text-sm text-slate-500">
        {{ $t('CO_LIVING.CATEGORIES.DELETE_NOTE') }}
      </p>
    </DeleteModal>
  </div>
</template>
